import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  EmptyPortContainer,
  AvailablePortsLabel,
  PortsListStack,
  PortsDivider,
} from './styles';
import { useTranslation } from 'react-i18next';
import { EthernetPort } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import { LanPortBox } from '../styles';
import PortWithPopper from '../../PortWithPopper';
import Box from '@mui/material/Box';

import LanDetails from '../LanDetails';
import { useState } from 'react';
import PonDetails from '../PonDetails';

interface ConnectionProps {
  selectedPort: LanPortType | PonPortType | null;
  equipment: EquipmentType;
  setSelectedPort: (port: LanPortType | PonPortType | null) => void;
}

const isPortConnected = (status: unknown): boolean => {
  const statusStr = String(status ?? '').toUpperCase();
  return statusStr === 'CONNECTED' || statusStr === 'UP';
};

const isPortEnabled = (enable: unknown): boolean => {
  const enableStr = String(enable || '');
  return enableStr === '1' || enableStr === 'true';
};

/** Port statistics display with modern design*/
function Connection({
  selectedPort,
  equipment,
  setSelectedPort,
}: ConnectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isPon, setIsPon] = useState(false);

  const handleSelection = (port: LanPortType | PonPortType, isPon: boolean) => {
    setIsPon(isPon);
    setSelectedPort(port);
  };

  if (
    selectedPort &&
    !isPon &&
    (selectedPort as LanPortType).macAddress?.value == null
  ) {
    return (
      <Box
        sx={{
          height: '150px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <EthernetPort size={48} color={theme.palette.grey['02']} />
        <Typography variant="caption" color={theme.palette.white['06']}>
          {t('equipment.noMacAddress')}
        </Typography>
      </Box>
    );
  }

  if (!selectedPort || selectedPort == null) {
    return (
      <EmptyPortContainer>
        {/* Ports Section */}
        <Stack direction="column" gap={2}>
          <AvailablePortsLabel variant="subtitle2">
            {t('equipment.availablePorts')}
          </AvailablePortsLabel>

          {/** LAN and PON ports share a single wrapping row */}
          <PortsListStack>
            {(equipment.lanPorts ?? []).map((port, index) => (
              <LanPortBox
                onClick={() =>
                  isPortEnabled(port.enable?.value) &&
                  handleSelection(port, false)
                }
                key={`lan-${index}`}
                role="button"
                tabIndex={0}
                isActive={isPortEnabled(port.enable?.value)}
                isPon={false}
                onKeyDown={(e) =>
                  e.key === 'Enter' && handleSelection(port, false)
                }
              >
                <PortWithPopper port={port} />
              </LanPortBox>
            ))}

            {equipment.pons && equipment.pons.length > 0 && (
              <PortsDivider orientation="vertical" sx={{}} flexItem />
            )}

            {(equipment.pons ?? []).map((pon, index) => (
              <LanPortBox
                onClick={() => {
                  if (!pon.enable || isPortEnabled(pon.enable?.value)) {
                    handleSelection(pon, true);
                  }
                }}
                key={`pon-${index}`}
                role="button"
                tabIndex={0}
                isActive={isPortConnected(pon.status?.value)}
                isPon={true}
                onKeyDown={(e) =>
                  e.key === 'Enter' && handleSelection(pon, true)
                }
              >
                <PortWithPopper port={pon} isPon />
              </LanPortBox>
            ))}
          </PortsListStack>
        </Stack>
      </EmptyPortContainer>
    );
  }

  const isConnected = isPortConnected(selectedPort?.status?.value);
  const isEnabled = isPortEnabled(selectedPort?.enable?.value);

  return isPon ? (
    <PonDetails
      selectedPort={selectedPort as PonPortType}
      isConnected={isConnected}
      isEnabled={isEnabled}
    />
  ) : (
    <LanDetails
      selectedPort={selectedPort as LanPortType}
      isConnected={isConnected}
      isEnabled={isEnabled}
    />
  );
}

export default Connection;
